import { cn } from "@newxsystems/shared";
import type { IIconProps } from "./Icon.props";

const safeIconName = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;

const fallbackLabel = (value: string | null | undefined) => {
  if (!value) return "?";

  const parts = value.split(/[-_\s]+/u).filter(Boolean);
  if (!parts.length) return "?";

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

const labelFromName = (value: string) =>
  value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const Icon = ({
  className,
  decorative = true,
  fallback,
  name,
  style,
  title,
  ...props
}: IIconProps) => {
  const iconName = name && safeIconName.test(name) ? name : undefined;

  if (!iconName) {
    return (
      <span
        aria-hidden={decorative || undefined}
        aria-label={decorative ? undefined : (title ?? name ?? "Icon")}
        className={cn(
          "inline-flex size-5 shrink-0 items-center justify-center text-[0.55em] font-semibold leading-none",
          className,
        )}
        role={decorative ? undefined : "img"}
        style={style}
        title={title}
        {...props}
      >
        {fallback ?? fallbackLabel(name)}
      </span>
    );
  }

  const iconPath = `/icons/${iconName}.svg`;
  const iconLabel = labelFromName(iconName);

  return (
    <span
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : (title ?? iconLabel)}
      className={cn("inline-block size-5 shrink-0 bg-current", className)}
      role={decorative ? undefined : "img"}
      style={{
        ...style,
        WebkitMask: `url("${iconPath}") center / contain no-repeat`,
        mask: `url("${iconPath}") center / contain no-repeat`,
      }}
      title={title}
      {...props}
    />
  );
};

Icon.displayName = "Ui::Icon";
